import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Pencil, Camera, ArrowUp, ArrowDown, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POI_TYPES = [
  // Segurança / avisos
  { value: 'warning', label: 'Aviso / Perigo', icon: '⚠️' },
  { value: 'obstacle', label: 'Obstáculo', icon: '🪨' },
  { value: 'gate', label: 'Portão / Cancela', icon: '🚧' },
  { value: 'ford', label: 'Vau / Passagem de rio', icon: '🌊' },
  // Natureza / paisagem
  { value: 'viewpoint', label: 'Miradouro', icon: '🌄' },
  { value: 'poi', label: 'Paisagem / PI', icon: '🏞️' },
  { value: 'summit', label: 'Cume / Pico', icon: '⛰️' },
  { value: 'forest', label: 'Floresta / Bosque', icon: '🌲' },
  { value: 'beach', label: 'Praia', icon: '🏖️' },
  { value: 'cave', label: 'Gruta / Cave', icon: '🕳️' },
  { value: 'waterfall', label: 'Cascata / Queda de água', icon: '💦' },
  { value: 'lake', label: 'Lago / Barragem', icon: '🏞️' },
  // Cultura / história
  { value: 'historic', label: 'Marco histórico', icon: '🏰' },
  { value: 'monument', label: 'Monumento', icon: '🗿' },
  { value: 'church', label: 'Igreja / Capela', icon: '⛪' },
  { value: 'ruins', label: 'Ruínas', icon: '🏛️' },
  { value: 'village', label: 'Aldeia / Povoação', icon: '🏘️' },
  { value: 'windmill', label: 'Moinho', icon: '🌾' },
  // Serviços úteis
  { value: 'fuel', label: 'Combustível', icon: '⛽' },
  { value: 'service', label: 'Restaurante / Comida', icon: '🍽️' },
  { value: 'cafe', label: 'Café / Bar', icon: '☕' },
  { value: 'lodging', label: 'Alojamento / Camping', icon: '🏕️' },
  { value: 'parking', label: 'Estacionamento', icon: '🅿️' },
  { value: 'water', label: 'Água / Fonte', icon: '💧' },
  { value: 'workshop', label: 'Oficina / Mecânico', icon: '🔧' },
  { value: 'shop', label: 'Loja / Mercearia', icon: '🛒' },
  { value: 'wc', label: 'WC / Sanitários', icon: '🚻' },
  // Marcadores logísticos
  { value: 'picnic', label: 'Merenda / Piquenique', icon: '🧺' },
  { value: 'photo', label: 'Ponto fotográfico', icon: '📸' },
  { value: 'start', label: 'Partida / Chegada', icon: '🏁' },
  { value: 'checkpoint', label: 'Checkpoint / Controlo', icon: '📍' },
];

const EMPTY_POI = {
  name: '',
  description: '',
  info: '',
  latitude: '',
  longitude: '',
  type: 'poi',
  alertRadius: '100',
  icon: '📍',
};

const POIFormFields = ({ data, setData, idPrefix }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-name`}>Nome *</Label>
      <Input
        id={`${idPrefix}-name`}
        value={data.name}
        onChange={(e) => setData({ ...data, name: e.target.value })}
        required
        placeholder="Ex: Miradouro da Serra"
        data-testid={`${idPrefix}-name-input`}
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-description`}>Descrição</Label>
      <Textarea
        id={`${idPrefix}-description`}
        value={data.description}
        onChange={(e) => setData({ ...data, description: e.target.value })}
        placeholder="Descrição curta do ponto"
        data-testid={`${idPrefix}-description-input`}
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-info`}>Informação adicional (visível na app)</Label>
      <Textarea
        id={`${idPrefix}-info`}
        value={data.info || ''}
        onChange={(e) => setData({ ...data, info: e.target.value })}
        placeholder="Ex: horários, contactos, avisos para o comboio, pista do rally paper..."
        data-testid={`${idPrefix}-info-input`}
      />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-lat`}>Latitude *</Label>
        <Input
          id={`${idPrefix}-lat`}
          type="number"
          step="0.000001"
          value={data.latitude}
          onChange={(e) => setData({ ...data, latitude: e.target.value })}
          required
          placeholder="40.3250"
          data-testid={`${idPrefix}-lat-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-lon`}>Longitude *</Label>
        <Input
          id={`${idPrefix}-lon`}
          type="number"
          step="0.000001"
          value={data.longitude}
          onChange={(e) => setData({ ...data, longitude: e.target.value })}
          required
          placeholder="-7.6080"
          data-testid={`${idPrefix}-lon-input`}
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-type`}>Tipo</Label>
        <Select
          value={data.type}
          onValueChange={(value) => {
            const selectedType = POI_TYPES.find((t) => t.value === value);
            setData({ ...data, type: value, icon: selectedType?.icon || '📍' });
          }}
        >
          <SelectTrigger id={`${idPrefix}-type`} data-testid={`${idPrefix}-type-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {POI_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.icon} {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-radius`}>Raio de Alerta (m)</Label>
        <Input
          id={`${idPrefix}-radius`}
          type="number"
          value={data.alertRadius}
          onChange={(e) => setData({ ...data, alertRadius: e.target.value })}
          placeholder="100"
          data-testid={`${idPrefix}-radius-input`}
        />
      </div>
    </div>
  </>
);

const POIManager = ({ route, onPOIsUpdated, onPOIFocus, isAdmin = true }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [poiData, setPoiData] = useState(EMPTY_POI);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(EMPTY_POI);
  const [editingId, setEditingId] = useState(null);
  // Info modal — abre quando o user clica num POI (janela de detalhes)
  const [infoPoi, setInfoPoi] = useState(null);
  // Reordenar POIs — chama backend POST /pois/reorder com nova sequência.
  const reorderPOI = async (poiId, direction) => {
    const pois = route.pois || [];
    const idx = pois.findIndex((p) => p.id === poiId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= pois.length) return;
    const newOrder = pois.map((p) => p.id);
    [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
    try {
      await axios.post(
        `${API}/routes/${route.route_id}/pois/reorder`,
        { order: newOrder },
        { withCredentials: true },
      );
      if (onPOIsUpdated) onPOIsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reordenar POI');
    }
  };

  const handleAddPOI = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const poi = {
        ...poiData,
        latitude: parseFloat(poiData.latitude),
        longitude: parseFloat(poiData.longitude),
        alertRadius: parseInt(poiData.alertRadius, 10),
      };
      await axios.post(`${API}/routes/${route.route_id}/pois`, poi, { withCredentials: true });
      toast.success('POI adicionado com sucesso!');
      setPoiData(EMPTY_POI);
      setOpen(false);
      if (onPOIsUpdated) onPOIsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar POI');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (poi) => {
    setEditingId(poi.id);
    setEditData({
      name: poi.name || '',
      description: poi.description || '',
      info: poi.info || '',
      latitude: String(poi.latitude ?? ''),
      longitude: String(poi.longitude ?? ''),
      type: poi.type || 'poi',
      alertRadius: String(poi.alertRadius ?? '100'),
      icon: poi.icon || '📍',
    });
    setEditOpen(true);
  };

  const handleEditPOI = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const patch = {
        ...editData,
        latitude: parseFloat(editData.latitude),
        longitude: parseFloat(editData.longitude),
        alertRadius: parseInt(editData.alertRadius, 10),
      };
      await axios.patch(`${API}/routes/${route.route_id}/pois/${editingId}`, patch, { withCredentials: true });
      toast.success('POI atualizado!');
      setEditOpen(false);
      if (onPOIsUpdated) onPOIsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar POI');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePOI = async (poiId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este POI?')) return;
    try {
      await axios.delete(`${API}/routes/${route.route_id}/pois/${poiId}`, { withCredentials: true });
      toast.success('POI eliminado');
      if (onPOIsUpdated) onPOIsUpdated();
    } catch (error) {
      toast.error('Erro ao eliminar POI');
    }
  };

  const handlePhotoUpload = async (poi, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    try {
      await axios.post(`${API}/routes/${route.route_id}/pois/${poi.id}/photo`, fd, { withCredentials: true });
      toast.success('Foto anexada ao POI 📷');
      if (onPOIsUpdated) onPOIsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar foto');
    }
  };

  return (
    <div className="space-y-4">
      {route.pois && route.pois.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">POIs da Rota:</h4>
          {route.pois.map((poi, idx) => (
            <div
              key={poi.id}
              className="flex items-start justify-between p-2 bg-muted rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
              data-testid={`poi-row-${poi.id}`}
              onClick={() => {
                setInfoPoi(poi);
                if (onPOIFocus) onPOIFocus(poi);
              }}
              title="Clicar para ver detalhes e focar no mapa"
            >
              <div className="flex items-start gap-2 min-w-0 flex-1">
                <span className="text-lg">{poi.icon || '📍'}</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-muted-foreground bg-background border border-border rounded px-1.5 py-0.5 flex-shrink-0">
                      #{idx + 1}
                    </span>
                    {poi.name}
                  </p>
                  {poi.description && <p className="text-xs text-muted-foreground line-clamp-1">{poi.description}</p>}
                  {poi.info && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1 whitespace-pre-wrap" data-testid={`poi-info-${poi.id}`}>
                      ℹ️ {poi.info}
                    </p>
                  )}
                  {poi.photo && (
                    <img
                      src={`${API}/routes/${route.route_id}/pois/${poi.id}/photo`}
                      alt={poi.name}
                      className="mt-1.5 rounded-md max-h-28 object-cover"
                      data-testid={`poi-photo-${poi.id}`}
                    />
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {Number(poi.latitude).toFixed(4)}, {Number(poi.longitude).toFixed(4)}{poi.alertRadius ? ` • ${poi.alertRadius}m` : ''}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={idx === 0}
                    onClick={() => reorderPOI(poi.id, 'up')}
                    data-testid={`up-poi-${poi.id}`}
                    title="Mover para cima"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={idx === route.pois.length - 1}
                    onClick={() => reorderPOI(poi.id, 'down')}
                    data-testid={`down-poi-${poi.id}`}
                    title="Mover para baixo"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => onPOIFocus && onPOIFocus(poi)}
                    data-testid={`focus-poi-${poi.id}`}
                    title="Focar no mapa"
                  >
                    <MapPin className="h-4 w-4" />
                  </Button>
                  <label
                    title="Anexar foto"
                    className="cursor-pointer inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent"
                    data-testid={`photo-poi-${poi.id}`}
                  >
                    <Camera className="h-4 w-4" />
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload(poi, e.target.files?.[0])}
                    />
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => openEdit(poi)}
                    data-testid={`edit-poi-${poi.id}`}
                    title="Editar POI"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleDeletePOI(poi.id)}
                    data-testid={`delete-poi-${poi.id}`}
                    title="Eliminar POI"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="no-pois-message">Esta rota ainda não tem POIs.</p>
      )}

      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full" data-testid="add-poi-button">
              <Plus className="mr-2 h-4 w-4" />
              Adicionar POI
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="poi-dialog" className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adicionar Ponto de Interesse</DialogTitle>
              <DialogDescription>Crie alertas e informação para pontos importantes na rota</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddPOI} className="space-y-4">
              <POIFormFields data={poiData} setData={setPoiData} idPrefix="poi" />
              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-poi-button">
                {loading ? 'A adicionar...' : 'Adicionar POI'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent data-testid="poi-edit-dialog" className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Ponto de Interesse</DialogTitle>
              <DialogDescription>Atualiza nome, informação ou localização do POI</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditPOI} className="space-y-4">
              <POIFormFields data={editData} setData={setEditData} idPrefix="poi-edit" />
              <Button type="submit" className="w-full" disabled={loading} data-testid="submit-poi-edit-button">
                {loading ? 'A guardar...' : 'Guardar alterações'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* POI Info Dialog — janela de detalhes quando se clica num POI (todos os users) */}
      <Dialog open={!!infoPoi} onOpenChange={(o) => !o && setInfoPoi(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="poi-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{infoPoi?.icon || '📍'}</span>
              {infoPoi?.name}
            </DialogTitle>
            {infoPoi?.description && (
              <DialogDescription>{infoPoi.description}</DialogDescription>
            )}
          </DialogHeader>
          {infoPoi && (
            <div className="space-y-3">
              {infoPoi.photo && (
                <img
                  src={`${API}/routes/${route.route_id}/pois/${infoPoi.id}/photo`}
                  alt={infoPoi.name}
                  className="w-full rounded-md max-h-64 object-cover"
                  data-testid={`poi-info-photo`}
                />
              )}
              {infoPoi.info && (
                <div className="p-3 rounded-md border" style={{ background: 'rgba(250,204,21,0.10)', borderColor: 'rgba(250,204,21,0.35)' }} data-testid="poi-info-notes">
                  <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: '#FACC15' }}>ℹ️ Informação</p>
                  <p className="text-sm whitespace-pre-wrap">{infoPoi.info}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded" style={{ background: 'rgba(196,162,117,0.08)', fontFamily: "'JetBrains Mono', monospace" }}>
                  <div className="uppercase tracking-wider text-muted-foreground">Latitude</div>
                  <div className="font-bold">{Number(infoPoi.latitude).toFixed(6)}</div>
                </div>
                <div className="p-2 rounded" style={{ background: 'rgba(196,162,117,0.08)', fontFamily: "'JetBrains Mono', monospace" }}>
                  <div className="uppercase tracking-wider text-muted-foreground">Longitude</div>
                  <div className="font-bold">{Number(infoPoi.longitude).toFixed(6)}</div>
                </div>
                {infoPoi.alertRadius && (
                  <div className="p-2 rounded col-span-2" style={{ background: 'rgba(232,122,42,0.08)', fontFamily: "'JetBrains Mono', monospace" }}>
                    <div className="uppercase tracking-wider text-muted-foreground">Raio de alerta</div>
                    <div className="font-bold">{infoPoi.alertRadius} m</div>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => {
                    if (onPOIFocus) onPOIFocus(infoPoi);
                    setInfoPoi(null);
                  }}
                  data-testid="poi-info-focus-map"
                >
                  🗺️ Focar no Mapa
                </Button>
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${infoPoi.latitude},${infoPoi.longitude}`;
                    window.open(url, '_blank');
                  }}
                  data-testid="poi-info-open-gmaps"
                >
                  ↗ Google Maps
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POIManager;
